import axios from 'axios';

// Enhanced axios instance with timeout and retry logic
const apiClient = axios.create({
  timeout: 15000, // 15 second timeout
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    // Removed User-Agent header as browsers don't allow it
  },
});

// Retry function with exponential backoff
export const fetchWithRetry = async (url, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const response = await apiClient.get(url);
      return response;
    } catch (error) {
      lastError = error;
      console.warn(`API call attempt ${attempt + 1} failed:`, error.message);
      
      // If it's a server error (5xx), retry with exponential backoff
      if (error.response && error.response.status >= 500) {
        const delay = Math.pow(2, attempt) * 1000; // Exponential backoff
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        // For other errors, don't retry
        break;
      }
    }
  }
  
  throw lastError;
};

// Enhanced error handling for API calls
export const handleApiError = (error, context = 'API call') => {
  if (error.response) {
    // Server responded with error status
    console.error(`${context} failed with status ${error.response.status}:`, error.response.data);
    return {
      error: true,
      message: `Server error: ${error.response.status}`,
      status: error.response.status,
      data: error.response.data
    };
  } else if (error.request) {
    // Request was made but no response received
    console.error(`${context} failed - no response received:`, error.message);
    return {
      error: true,
      message: 'Network error - no response received',
      status: 'NETWORK_ERROR'
    };
  } else {
    // Something else happened
    console.error(`${context} failed:`, error.message);
    return {
      error: true,
      message: error.message,
      status: 'UNKNOWN_ERROR'
    };
  }
};

// Utility to check if error is network-related
export const isNetworkError = (error) => {
  return error.code === 'ERR_NETWORK' || 
         error.message.includes('Network Error') ||
         error.response?.status >= 500;
};

// Function to test if API server is reachable
export const testApiServer = async () => {
  try {
    const response = await apiClient.get('/api/health');
    console.log('API server test successful:', response.data);
    return true;
  } catch (error) {
    console.log('API server test failed:', error.message);
    return false;
  }
}; 